import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, LogOut, Plus, CalendarRange, Trash2, Edit3,
  Building2, Layers, Settings,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import DatePickerField from "@/components/DatePickerField";
import { fmtDate } from "@/lib/format";
import ShowEditor from "@/pages/ShowEditor";
import ImpersonationBanner from "@/components/ImpersonationBanner";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

/**
 * Season Planner — the first real page inside the Admin Dashboard.
 *
 * Layout: seasons list on the left, timeline of shows on the right.
 * • Org Admin (and super_admin) can CRUD seasons and move productions
 *   between them.
 * • Producer sees every season + every show (read-only from here).
 * • Renders standalone (like AdminDashboard) — this is an org-level page
 *   that must work before a production is selected.
 */
export default function SeasonPlanner() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const canEdit = user?.role === "org_admin" || user?.role === "super_admin";

  const [seasons, setSeasons] = useState([]);
  const [productions, setProductions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dialog, setDialog] = useState(null); // { mode, season?: {} }
  const [form, setForm] = useState({ id: null, name: "", start_date: "", end_date: "", color: "#F59E0B", notes: "" });
  const [showEditor, setShowEditor] = useState(null); // { production } — null means closed; { production: null } = new

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/seasons"), api.get("/productions")]);
    setSeasons(s.data);
    setProductions(p.data);
    if (!selectedId && s.data[0]) setSelectedId(s.data[0].id);
  };
  useEffect(() => { load(); }, []);

  const openDialog = (season) => {
    setForm(season
      ? { id: season.id, name: season.name, start_date: season.start_date || "", end_date: season.end_date || "", color: season.color || "#F59E0B", notes: season.notes || "" }
      : { id: null, name: "", start_date: "", end_date: "", color: "#F59E0B", notes: "" });
    setDialog({ mode: season ? "edit" : "new" });
  };

  const saveSeason = async () => {
    if (!form.name.trim()) return toast.error("Season name is required");
    try {
      const body = {
        name: form.name.trim(),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        color: form.color,
        notes: form.notes,
      };
      const r = form.id ? await api.patch(`/seasons/${form.id}`, body) : await api.post("/seasons", body);
      toast.success(form.id ? "Season updated" : "Season created");
      setDialog(null);
      await load();
      if (!form.id && r.data?.id) setSelectedId(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeSeason = async (id) => {
    if (!confirm("Delete this season? Any shows attached will be moved to 'Unassigned'.")) return;
    await api.delete(`/seasons/${id}`);
    if (selectedId === id) setSelectedId(null);
    load();
  };

  const moveShowToSeason = async (prodId, seasonId) => {
    try {
      await api.patch(`/productions/${prodId}`, { season_id: seasonId || null });
      toast.success("Show moved");
      load();
    } catch { toast.error("Failed to move show"); }
  };

  const showsBySeason = useMemo(() => {
    const m = { __none: [] };
    seasons.forEach((s) => { m[s.id] = []; });
    productions.forEach((p) => {
      const key = p.season_id && m[p.season_id] !== undefined ? p.season_id : "__none";
      m[key].push(p);
    });
    // sort within each bucket by opening_night
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.opening_night || "").localeCompare(b.opening_night || "")));
    return m;
  }, [seasons, productions]);

  const selected = seasons.find((s) => s.id === selectedId);
  const currentShows = selectedId === "__none"
    ? showsBySeason.__none
    : (selectedId ? showsBySeason[selectedId] : []) || [];

  return (
    <div className="min-h-screen bg-black text-white">
      <ImpersonationBanner />
      <header className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <button
            data-testid="season-back"
            onClick={() => nav("/admin/dashboard")}
            className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Admin Dashboard
          </button>
          <div className="text-right hidden md:block">
            <div className="text-sm text-white">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{user?.role}</div>
          </div>
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            className="h-9 px-3 rounded-md border border-white/15 text-zinc-300 hover:text-white hover:bg-white/5 text-sm inline-flex items-center gap-1.5"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-6 animate-fade-up" data-testid="season-planner">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Org Admin</div>
            <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Season Planner</h1>
            <p className="mt-1 text-zinc-400 text-sm max-w-xl">
              Group shows into seasons. Move a show between seasons in a click. Producer and Org Admin see everything —
              company members only see the seasons that contain shows they&apos;re on.
            </p>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button
                data-testid="add-show-btn"
                onClick={() => setShowEditor({ production: null })}
                className="h-10 px-4 rounded-md border border-gold/40 text-gold hover:bg-gold/10 font-semibold text-sm inline-flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> New show
              </button>
              <button
                data-testid="add-season-btn"
                onClick={() => openDialog(null)}
                style={goldBtn}
                className="h-10 px-4 rounded-md font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110"
              >
                <Plus className="w-4 h-4" /> New Season
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
          {/* Season rail */}
          <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-3 space-y-1">
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-2 px-2">Seasons</div>
            {seasons.length === 0 && (
              <div className="text-xs text-zinc-500 italic py-6 text-center">
                No seasons yet. Create one to start grouping shows.
              </div>
            )}
            <ul className="space-y-1">
              {seasons.map((s) => (
                <li key={s.id}>
                  <button
                    data-testid={`season-item-${s.id}`}
                    onClick={() => setSelectedId(s.id)}
                    className={`w-full flex items-center gap-2 px-2 py-2 rounded text-left text-sm transition-colors
                      ${selectedId === s.id ? "bg-gold/10 text-gold border-l-2" : "hover:bg-white/5 border-l-2 border-transparent"}`}
                    style={{ borderLeftColor: selectedId === s.id ? s.color || "#F59E0B" : "transparent" }}
                  >
                    <Layers className="w-3.5 h-3.5 shrink-0" style={{ color: s.color || "#F59E0B" }} />
                    <span className="flex-1 truncate">{s.name}</span>
                    <span className="text-[10px] text-zinc-500">{s.production_count}</span>
                  </button>
                </li>
              ))}
              <li>
                <button
                  data-testid="season-item-none"
                  onClick={() => setSelectedId("__none")}
                  className={`w-full flex items-center gap-2 px-2 py-2 rounded text-left text-sm transition-colors mt-2 border-t border-white/5 pt-3
                    ${selectedId === "__none" ? "bg-white/5 text-white" : "hover:bg-white/5 text-zinc-400"}`}
                >
                  <CalendarRange className="w-3.5 h-3.5 shrink-0" />
                  <span className="flex-1 truncate">Unassigned</span>
                  <span className="text-[10px] text-zinc-500">{showsBySeason.__none.length}</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Detail */}
          <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-5 space-y-4">
            {selectedId === "__none" ? (
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Unassigned</div>
                <div className="font-display text-2xl">Shows not in any season</div>
                <p className="text-sm text-zinc-500 mt-1">Move them into a season below.</p>
              </div>
            ) : selected ? (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Season</div>
                  <div className="font-display text-2xl">{selected.name}</div>
                  <div className="mt-1 text-sm text-zinc-500">
                    {selected.start_date || selected.end_date
                      ? `${selected.start_date ? fmtDate(selected.start_date) : "—"} → ${selected.end_date ? fmtDate(selected.end_date) : "—"}`
                      : "No dates set"}
                  </div>
                  {selected.notes && <p className="text-sm text-zinc-400 mt-2 max-w-lg">{selected.notes}</p>}
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <button data-testid="edit-season-btn" onClick={() => openDialog(selected)} className="h-8 px-2 rounded border border-white/10 hover:border-gold hover:text-gold text-xs inline-flex items-center gap-1"><Edit3 className="w-3 h-3" /> Edit</button>
                    <button data-testid="delete-season-btn" onClick={() => removeSeason(selected.id)} className="h-8 px-2 rounded border border-white/10 hover:border-red-500 hover:text-red-400 text-xs inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-zinc-500 italic py-6">Pick a season on the left to see the shows in it.</div>
            )}

            <div className="pt-4 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-2">Shows in this season</div>
              {currentShows.length === 0 ? (
                <div className="text-sm text-zinc-500 italic py-6 text-center">No shows here yet.</div>
              ) : (
                <ul className="space-y-2">
                  {currentShows.map((p) => (
                    <li key={p.id} className="rounded-md border border-white/10 bg-[#121212] p-3 flex items-center gap-3" data-testid={`season-show-${p.id}`}>
                      <Building2 className="w-4 h-4 text-gold shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-white truncate">{p.title}</div>
                        <div className="text-[11px] text-zinc-500">
                          {p.venue ? `${p.venue} · ` : ""}{p.opening_night ? `Opens ${fmtDate(p.opening_night)}` : "No opening date"}
                        </div>
                      </div>
                      {canEdit && (
                        <>
                          <Select value={p.season_id || "__none"} onValueChange={(v) => moveShowToSeason(p.id, v === "__none" ? "" : v)}>
                            <SelectTrigger data-testid={`season-move-${p.id}`} className="w-40 h-8 bg-[#0A0A0A] border-white/10 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#121212] border-white/10 text-white">
                              <SelectItem value="__none">Unassigned</SelectItem>
                              {seasons.map((s) => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            onClick={() => setShowEditor({ production: p })}
                            data-testid={`manage-show-${p.id}`}
                            className="h-8 px-2 rounded border border-white/10 hover:border-gold hover:text-gold text-xs inline-flex items-center gap-1"
                          >
                            <Settings className="w-3 h-3" /> Manage
                          </button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Create / edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{form.id ? "Edit season" : "New season"}</DialogTitle>
            <DialogDescription className="text-zinc-500">A season groups a handful of productions across a period of time — e.g. &quot;2026–27 Mainstage&quot;.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="season-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="2026–27 Mainstage" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start date</Label><DatePickerField value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v || "" })} /></div>
              <div><Label>End date</Label><DatePickerField value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v || "" })} /></div>
            </div>
            <div><Label>Colour</Label><input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-full rounded bg-[#0A0A0A] border border-white/10" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="Themes, funders, anything worth remembering." /></div>
          </div>
          <DialogFooter>
            <button data-testid="save-season-btn" onClick={saveSeason} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Full show editor — create + all Settings-tier fields in one place */}
      {showEditor && (
        <ShowEditor
          open={!!showEditor}
          production={showEditor.production}
          seasons={seasons}
          onClose={() => setShowEditor(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
